/**
 * The partner ledger's shared logic (TMMPDM-0095, TMMAPI-0162 U3/U4): the payee ref a partner's ledger
 * is addressed by, the links to a Ledger, the words for line kinds and the mode, and the
 * ledger mode read. The chips and banners that use them are in `ledgerShared.tsx`.
 *
 * Nothing here decides anything from two fields: a status, a hold, a mode is the one value the server
 * sent, shown in words. A kind the console doesn't know yet is shown by its code, never dropped.
 * TMMPDM-0098: `usePayoutActionsRetired` reads `payoutActionsRetired` alone. The field is required.
 * A value that is not `true` is not retired. It never reads the mode and never reads the FREEZE step.
 */
import { useCallback, useEffect, useState } from 'react';
import { adminApi } from '@/api/tmm/admin';
import { ApiError } from '@/api/errors';
import { formatMonthYear } from '@/format';
import { LEDGER_MODES, type LedgerMode } from '@/api/tmm/types';

export type Translate = (
  key: string,
  fallback?: string,
  values?: Record<string, string | number>,
) => string;

/**
 * The payee ref tmm-api keeps for a partner (`ConnectAccount.payeeRef`, `"<kind>:<id>"`; the backfill's
 * `AFFILIATE:` refs, TMMAPI-0161). It addresses the ledger in a path; tmm-api ownership-checks it.
 */
export function payeeRefOf(kind: 'RESELLER' | 'AFFILIATE', id: number): string {
  return `${kind}:${id}`;
}

/** The partner page's Ledger leaf, for a reseller. */
export function resellerLedgerPath(resellerId: number): string {
  return `/partners/${encodeURIComponent(String(resellerId))}?tab=ledger`;
}

/** An affiliate's Ledger section, through the tenant's partner route. */
export function affiliateLedgerPath(tenantSlug: string, affiliateId: number): string {
  return `/partners/by-tenant/${encodeURIComponent(tenantSlug)}/affiliates/${encodeURIComponent(String(affiliateId))}?tab=ledger`;
}

/** The 404 codes a deployed ledger route answers: what it couldn't find, worded by its code. */
const NOT_FOUND_CODES: ReadonlySet<string> = new Set([
  'PAYEE_NOT_FOUND',
  'STATEMENT_NOT_FOUND',
  'HOLD_NOT_FOUND',
]);

/**
 * A 404 from a ledger route that names nothing it couldn't find: tmm-api hasn't deployed the route
 * yet (the merge gate runs our way). A coded 404 (`PAYEE_NOT_FOUND`, …) is a deployed route's answer.
 */
export function isNotDeployed(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 404 && !NOT_FOUND_CODES.has(String(error.code))
  );
}

/** "September 2026" for a `YYYY-MM` period; the raw value if it isn't one; "—" when it wasn't sent. */
export function periodLabel(periodMonth: string | undefined, locale: string): string {
  if (typeof periodMonth !== 'string' || periodMonth === '') return '—';
  return /^\d{4}-\d{2}$/.test(periodMonth)
    ? formatMonthYear(`${periodMonth}-01`, locale)
    : periodMonth;
}

const LINE_FALLBACK: Record<string, string> = {
  REVENUE_SHARE: 'Revenue share',
  MARGIN: 'Margin',
  AFFILIATE_COMMISSION: 'Affiliate commission',
  COMMISSION_DEDUCTION: 'Commission deduction',
  PROCESSING_FEE: 'Processing fee',
  AFFILIATE_BONUS: 'Affiliate bonus',
  MINIMUM_SHORTFALL: 'Minimum shortfall',
  ADJUSTMENT: 'Adjustment',
  REVERSAL: 'Reversal',
  VOID: 'Void',
  PAYOUT: 'Payout',
  PAYOUT_MANUAL: 'Manual payment',
  PAYOUT_RETURNED: 'Payout returned',
  COLLECTION: 'Collection',
};

/** A line's kind in words; a kind the console doesn't know is shown by its code; "—" when absent. */
export function lineKindLabel(kind: string | undefined, t: Translate): string {
  if (typeof kind !== 'string' || kind === '') return '—';
  return Object.hasOwn(LINE_FALLBACK, kind)
    ? t(`ledger.lineKind.${kind}`, LINE_FALLBACK[kind])
    : kind;
}

const MODE_FALLBACK: Record<LedgerMode, string> = {
  OFF: 'Off',
  SHADOW: 'Shadow',
  LIVE: 'Live',
};

/** The ledger mode in words. */
export function ledgerModeLabel(mode: LedgerMode, t: Translate): string {
  return t(`ledger.mode.${mode}`, MODE_FALLBACK[mode]);
}

/** The mode a settings view sent, when it is one the contract names; otherwise none. */
export function ledgerModeOf(value: unknown): LedgerMode | null {
  return typeof value === 'string' && (LEDGER_MODES as readonly string[]).includes(value)
    ? (value as LedgerMode)
    : null;
}

/**
 * The ledger mode, read from the billing settings view (TMMAPI-0162 item 13). `null` while it is read,
 * when the view doesn't serve it yet, or when the read fails: then today's payout screens behave as
 * they always have. One read per screen visit; `reload` re-reads it (after a 410).
 */
export function useLedgerMode(): { mode: LedgerMode | null; reload: () => void } {
  const [mode, setMode] = useState<LedgerMode | null>(null);
  const [token, setToken] = useState(0);
  useEffect(() => {
    let cancelled = false;
    adminApi
      .getProductSettings()
      .then((view) => {
        if (!cancelled) setMode(ledgerModeOf(view.ledgerMode));
      })
      .catch(() => {
        if (!cancelled) setMode(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);
  const reload = useCallback(() => setToken((n) => n + 1), []);
  return { mode, reload };
}

/**
 * Whether the old payout actions are retired (TMMAPI-0162 item 36b, required by 0164).
 * `payoutActionsRetired === true` alone. Anything else is not retired. Never the mode, never FREEZE.
 */
export function payoutActionsRetiredOf(view: object | null | undefined): boolean {
  const flag = (view as { payoutActionsRetired?: unknown } | null | undefined)
    ?.payoutActionsRetired;
  return flag === true;
}

/**
 * The retire flag, from the same product-settings read as the mode. `false` while it is read or
 * when the read fails: today's payout screens then behave as they always have. One read per screen
 * visit; `reload` re-reads it (after a 410).
 */
export function usePayoutActionsRetired(): { retired: boolean; reload: () => void } {
  const [retired, setRetired] = useState(false);
  const [token, setToken] = useState(0);
  useEffect(() => {
    let cancelled = false;
    adminApi
      .getProductSettings()
      .then((view) => {
        if (!cancelled) setRetired(payoutActionsRetiredOf(view));
      })
      .catch(() => {
        if (!cancelled) setRetired(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);
  const reload = useCallback(() => setToken((n) => n + 1), []);
  return { retired, reload };
}
